import { Link } from 'react-router-dom';
import { Truck, Phone, Mail, MapPin, Facebook, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-zinc-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-6 bg-white px-4 py-3" data-testid="footer-logo">
              <img 
                src="/logo.png" 
                alt="Red Ball Express Logistics" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              Red Ball Express Logistics is a trusted U.S.-based asset-based trucking company delivering fast, reliable, and tailored logistics solutions nationwide.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-zinc-800 hover:bg-red-600 flex items-center justify-center transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-zinc-800 hover:bg-red-600 flex items-center justify-center transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-zinc-800 hover:bg-red-600 flex items-center justify-center transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-6">Quick Links</h4>
            <ul className="space-y-1">
              {[
                { name: 'Home', path: '/' },
                { name: 'Load Board', path: '/load-board' },
                { name: 'Our Services', path: '/services' },
                { name: 'Careers', path: '/careers' },
                { name: 'Contact Us', path: '/contact' },
                { name: 'Get a Quote', path: '/quote' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="block py-2 text-zinc-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-6">Services</h4>
            <ul className="space-y-1">
              {[
                { name: 'Full Truckload (FTL)', path: '/services/ftl' },
                { name: 'Less Than Truckload (LTL)', path: '/services/ltl' },
                { name: 'Intermodal Freight', path: '/services/intermodal' },
                { name: 'Drayage Services', path: '/services/drayage' },
                { name: 'Refrigerated Shipments', path: '/services/reefer' },
                { name: 'Air Freight', path: '/services/air-freight' },
                { name: 'Ocean Freight', path: '/services/ocean-freight' },
                { name: 'Warehousing', path: '/services/warehousing' },
                { name: 'Cross-Border', path: '/services/cross-border' },
                { name: 'Customs Clearance', path: '/services/customs-clearance' },
                { name: 'Hazmat Shipping', path: '/services/hazmat' },
                { name: 'Oversize & Heavy Haul', path: '/services/oversize' },
              ].map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="block py-2 text-zinc-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-6">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-zinc-400 text-sm space-y-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">Headquarters</div>
                    30 N Gould St #58539<br />
                    Sheridan, WY 82801, USA
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">West Coast Office</div>
                    3111 Camino Del Rio North #400<br />
                    San Diego, CA 92108, USA
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a 
                  href="tel:+18776809272" 
                  className="block py-2 text-zinc-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  1 (877) 680-9272
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a 
                  href="mailto:support@redballxpress.us" 
                  className="block py-2 text-zinc-400 hover:text-white transition-colors text-sm break-all"
                  data-testid="footer-email"
                >
                  support@redballxpress.us
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} Red Ball Express Logistics LLC. All Rights Reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-zinc-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-zinc-500 hover:text-white text-sm transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
